import { useMemo, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import styles from './chat-box.module.css';

const getSocketURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://server-production-a1b2.up.railway.app/';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const socket = io(getSocketURL(), { 
  transports: ['websocket', 'polling'],
  withCredentials: true // Important for CORS with credentials
});


const ChatBox = ({ recipientId, recipientName, headPage, onClose, imgSrc }) => {
    const [roomId, setRoomId] = useState('');
    const [senderId, setSenderId] = useState('');
    const [userId, setUserId] = useState('');
    const [messages, setMessages] = useState([]);
    const [recentMessage, setRecentMessage] = useState('');
    const [reloadMessage, setReloadMessage] = useState('');
    const [showOptions, setShowOptions] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        const fetchRoomId = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.post('/api/recruiter-to-applicant-message/start-chat', {
                    applicantId: recipientId,
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                // console.log("inside chatClick, roomId:fe1",roomId)
                setRoomId(response.data.roomId);
                setSenderId(response.data.userId);
                fetchMessages(response.data.roomId);
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred';
                console.error(errorMessage);
            }
        };

        fetchRoomId();
    }, [recipientId]);

    useEffect(() => {
        if (!roomId) return;

        socket.emit('joinRoom', { roomId });

        socket.on('receiveMessage', (newMessage) => {
            setReloadMessage(newMessage);
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        });

        return () => {
            socket.off('receiveMessage');
            if (roomId) {
                socket.emit('leaveRoom', { roomId });
            }
        };
    }, [roomId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const fetchMessages = async (roomId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/recruiter-to-applicant-message/get-messages', {
                recruiterRoomId: roomId,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.data.success) {
                // console.log("response.data:jcb2", response.data);
                const filteredMessages = response.data.messages.filter(msg => !msg.deletedFor.includes(response.data.userId));
                setMessages(filteredMessages);
                // console.log("filteredMessages:cb", filteredMessages);
                setUserId(response.data.userId);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error fetching messages';
            console.error(errorMessage);
        }
    };

    const handleSendMessage = async () => {
        // console.log("send-messages before currentRoomId:", roomId);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/recruiter-to-applicant-message/send-message', {
                recruiterRoomId: roomId,
                // senderId,
                receiverId: recipientId,
                message: recentMessage
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            // console.log("send-messages newMessage after", response.data.newMessage);
            if (response.data.success) {
                const newMessage = response.data.newMessage;
                socket.emit('sendMessage', newMessage);
                setMessages((prevMessages) => [...prevMessages, newMessage]);
            }
            setRecentMessage('');

        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error sending message';
            console.error(errorMessage);
        }
    };

    const handleMarkRead = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicant-message/mark-read', {
                messageId,
                userId: senderId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setMessages((prevMessages) => prevMessages.map(msg => {
                if (msg._id === messageId) {
                    return { ...msg, readBy: [...msg.readBy, { userId: userId, readAt: new Date() }] };
                }
                return msg;
            }));

        } catch (error) {
            console.error('Error marking message as read:', error.message);
        }
    };

    const handleDeleteMessageForMe = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicant-message/delete-message-for-me', {
                messageId,
                // userId: senderId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            console.error('Error deleting message for me:', error.message);
        }
    };

    const handleDeleteMessageForAll = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicant-message/delete-message-for-all', {
                messageId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            console.error('Error deleting message for all:', error.message);
        }
    };

    const handleClickOutside = (event) => {
        if (!event.target.closest(`.${styles.messageOptionsButton}`) && !event.target.closest(`.${styles.messageOptionsMenu}`)) {
            setShowOptions(null);
        }
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const isThisYear = d.getFullYear() === now.getFullYear();
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return isThisYear ? `${hours}:${minutes} ${day}/${month}` : `${hours}:${minutes} ${day}/${month}/${year}`;
    };

    const getProfileColor = (identifier) =>{
        let baseColors = ["#9BEEEE", "#EE9BEE", "#EE9BA2", "#9BEEA9", "#D9EE9B", "#9bb8ee", "#9beecf", "#9bdcee", "#b19bee", "#ee9bcf", "#cf9bee", "#9c9bee", "#9bc9ee", "#9beeb7", "#a6ee9b", "#daee9b", "#eeed9b", "#ed8b8b",];
        
        // Create a hash-like index based on the identifier (e.g., recipientName)
        let index = 0;
        for (let i = 0; i < identifier.length; i++) {
            index += identifier.charCodeAt(i);
        }
        return baseColors[index % baseColors.length]; // Always return the same color for the same identifier
    }
    const bgColor = useMemo(() => getProfileColor(recipientName), [recipientName]);


    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.recruiterChatBox}>
            {headPage === 'Application' && (
            <div className={styles.recruiterChatBoxHeader}>
                <h3>Send Message to {recipientName}</h3>
                <button onClick={onClose}>Close</button>
            </div>
            )}
            {headPage === 'Messages' && (
                <div className={styles.recruiterChatBoxHeader}>
                    <div>
                    <h3>{recipientName}</h3>
                    <img src={imgSrc ? imgSrc.replace('/upload/', '/upload/w_192,h_200,c_fill/') : '/default-profile.png'}
                     alt="Profile" className={styles.profilePictureHeader} 
                     style={{ backgroundColor: bgColor }} // Apply consistent color
                     />
                    </div>
                    <button onClick={onClose} >Close</button>
                </div>
            )}
            <div className={styles.messagesContainer}>
                {messages.map((msg, index) => (
                    <div key={msg._id} className={`${styles.message} ${msg.senderId === userId ? styles.sent : styles.received}`}>
                        <div className={styles.messageContent}>
                            {msg.message}
                            <button className={styles.messageOptionsButton} onClick={() => setShowOptions(showOptions === index ? null : index)}>⋮</button>
                            {showOptions === index && (
                                <div className={styles.messageOptionsMenu}>
                                    <button className={styles.messageOptionsMenuButton} onClick={() => handleMarkRead(msg._id)}>Read by</button>
                                    <button className={styles.messageOptionsMenuButton} onClick={() => handleDeleteMessageForMe(msg._id)}>Delete for me</button>
                                    {msg.senderId === userId && (
                                        <button className={styles.messageOptionsMenuButton} onClick={() => handleDeleteMessageForAll(msg._id)}>Delete for everyone</button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.messageTimestamp}>
                            {formatDate(msg.createdAt)}
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>
            <textarea
                className={styles.recruiterChatBoxTextarea}
                value={recentMessage}
                onChange={(e) => setRecentMessage(e.target.value)}
                placeholder="Type your message here..."
            />
            <button className={styles.recruiterChatBoxButton} onClick={handleSendMessage}>Send</button>
        </div>
    );
};

export default ChatBox;